import type { Color } from "../colors";
import { moment } from "../date";
import type { Locale, TranslationKey } from "../locales";
import { guardString } from "../strings";
import type { Enums, IncomingInvoiceEventListItem } from "../types";
import type {
	IncomingInvoiceCheckDetails,
	IncomingInvoiceCheckGroup,
	IncomingInvoiceCheckKey,
} from "./checks";

/**
 * How an incoming invoice reads on a screen — its dates, amounts, states and timeline — written
 * once for the web app and the native app, so the two never word or colour an invoice apart.
 */

/**
 * A date column of an invoice, in the app's date notation.
 *
 * @param value - The `YYYY-MM-DD` date, or null when it was not read.
 * @returns The formatted date, or the dash every empty field shows.
 */
export function formatInvoiceDate(value: string | null) {
	return value ? moment(value).dateFormat() : guardString(null);
}

/**
 * An amount in the invoice's own currency — German notation on the screens, and the reader's
 * where a text is sent to a person in their language. Zero is shown as zero: a reverse-charge
 * invoice carries 0 VAT on purpose, and a dash there would read as "not read".
 *
 * @param value - The amount, or null when it was not read.
 * @param currency - The invoice's ISO currency code.
 * @param language - The notation to use; German when left out.
 * @returns The formatted amount, or the dash every empty field shows.
 */
export function formatInvoiceAmount(
	value: number | null,
	currency: string,
	language: Locale = "de",
) {
	if (value === null) return guardString(null);
	const locale = language === "en" ? "en-GB" : "de-DE";
	return value.toLocaleString(locale, { style: "currency", currency });
}

/**
 * A rate in percent, German notation, with as many decimals as it carries — 2,5 % Skonto stays
 * 2,5 % rather than rounding to 3 %.
 *
 * @param value - The rate, e.g. 19 for 19 %, or null when it was not read.
 * @returns The formatted rate, or the dash every empty field shows.
 */
export function formatInvoiceRate(value: number | null) {
	if (value === null) return guardString(null);
	return `${value.toLocaleString("de-DE", { maximumFractionDigits: 2 })} %`;
}

/**
 * The locale key a reminder's level reads as: the first, second or final reminder, or a plain
 * reminder when the document did not say.
 *
 * @param level - 1 to 3, or null.
 */
export function reminderLevelLabel(level: number | null): TranslationKey {
	if (level === 1) return "incoming_invoices.reminders.level_1";
	if (level === 2) return "incoming_invoices.reminders.level_2";
	if (level === 3) return "incoming_invoices.reminders.level_3";
	return "incoming_invoices.reminders.reminder";
}

/** Why an invoice's IBAN holds its approval up. */
export type IbanCheckIssueKind = "new-iban" | "suspected-fraud" | "unknown-partner-iban";

/**
 * What an invoice's IBAN asks of its approvers before they may approve it: a first account for a
 * contact that has none (`new-iban`), one beside the accounts it already has, which is how a
 * forged invoice reads (`suspected-fraud`), or a partner's IBAN none of its workspaces carries
 * (`unknown-partner-iban`), which the partner settles rather than an approver. Null while
 * `iban-known` does not fail.
 *
 * @param checks - The invoice's review checks.
 * @returns The issue and the IBAN it concerns, or null.
 */
export function getIbanCheckIssue(
	checks: ReadonlyArray<{
		checkKey: string;
		state: string;
		details: IncomingInvoiceCheckDetails | null;
	}>,
): { kind: IbanCheckIssueKind; iban: string | null } | null {
	for (const check of checks) {
		if (check.checkKey !== "iban-known" || check.state !== "failed") continue;
		const reason = check.details?.reason;
		const kind =
			reason === "new-iban" || reason === "unknown-partner-iban" ? reason : "suspected-fraud";
		return { kind, iban: check.details?.iban ?? null };
	}
	return null;
}

/** The locale keys an IBAN issue's notice reads with, per kind. */
export const IBAN_CHECK_ISSUE_TEXT = {
	"new-iban": {
		title: "incoming_invoices.iban_check.new_title",
		description: "incoming_invoices.iban_check.new_description",
	},
	"suspected-fraud": {
		title: "incoming_invoices.iban_check.fraud_title",
		description: "incoming_invoices.iban_check.fraud_description",
	},
	"unknown-partner-iban": {
		title: "incoming_invoices.iban_check.partner_title",
		description: "incoming_invoices.iban_check.partner_description",
	},
} as const satisfies Record<
	IbanCheckIssueKind,
	{ title: TranslationKey; description: TranslationKey }
>;

/**
 * Whether an invoice's fields and cost allocation are out of reach for edits: a paid one until its
 * payment is taken back, an approved one until its review is reset — what its approvers signed
 * off stays as they saw it. The server refuses both; the clients disable the edits to match.
 *
 * @param state - The invoice's state.
 */
export function isIncomingInvoiceLocked(state: Enums<"incomingInvoiceState">) {
	return state === "paid" || state === "approved";
}

/**
 * The checks the review itself settles, which a failed-checks warning in it leaves out: the cost
 * allocation is set in the review, and an unconfirmed IBAN blocks accepting with its own notice.
 */
const SETTLED_BY_REVIEW: ReadonlySet<string> = new Set(["cost-allocation-complete", "iban-known"]);

/**
 * How many of an invoice's checks failed that the person reviewing it should know about before
 * accepting — every failed one but those the review settles itself.
 *
 * @param checks - The invoice's review checks.
 */
export function countReviewWarnings(checks: ReadonlyArray<{ checkKey: string; state: string }>) {
	let count = 0;
	for (const check of checks) {
		if (check.state === "failed" && !SETTLED_BY_REVIEW.has(check.checkKey)) count++;
	}
	return count;
}

/** The colour an invoice's state is drawn in. */
export const INCOMING_INVOICE_STATE_COLOR: Record<Enums<"incomingInvoiceState">, Color> = {
	received: "gray",
	approved: "emerald",
	paid: "emerald",
	rejected: "red",
	duplicate: "orange",
	merged: "slate",
};

/** The colour one approval's state is drawn in. */
export const INCOMING_INVOICE_APPROVAL_STATE_COLOR: Record<
	Enums<"incomingInvoiceApprovalState">,
	Color
> = {
	pending: "amber",
	approved: "emerald",
	rejected: "rose",
	skipped: "gray",
};

/** The locale key per kind: the enum spells its values with hyphens, locale keys are snake_case. */
export const INCOMING_INVOICE_KIND_LABEL = {
	invoice: "incoming_invoices.kind.invoice",
	"partial-invoice": "incoming_invoices.kind.partial_invoice",
	"final-invoice": "incoming_invoices.kind.final_invoice",
	"credit-note": "incoming_invoices.kind.credit_note",
} as const satisfies Record<Enums<"incomingInvoiceKind">, TranslationKey>;

/** The locale key per review check: check keys use hyphens, locale keys are snake_case. */
export const INCOMING_INVOICE_CHECK_LABEL = {
	"number-present": "incoming_invoices.checks.number_present",
	"issuer-assigned": "incoming_invoices.checks.issuer_assigned",
	"invoice-date-present": "incoming_invoices.checks.invoice_date_present",
	"tax-id-known": "incoming_invoices.checks.tax_id_known",
	"iban-known": "incoming_invoices.checks.iban_known",
	"no-duplicate": "incoming_invoices.checks.no_duplicate",
	"gross-matches-net-plus-vat": "incoming_invoices.checks.gross_matches_net_plus_vat",
	"positions-match-net": "incoming_invoices.checks.positions_match_net",
	"cost-allocation-complete": "incoming_invoices.checks.cost_allocation_complete",
} as const satisfies Record<IncomingInvoiceCheckKey, TranslationKey>;

/** The locale key per group of review checks. */
export const INCOMING_INVOICE_CHECK_GROUP_LABEL = {
	formal: "incoming_invoices.check_groups.formal",
	arithmetic: "incoming_invoices.check_groups.arithmetic",
} as const satisfies Record<IncomingInvoiceCheckGroup, TranslationKey>;

/** The sentence a timeline event reads as, after the person or the system who did it. */
export const INCOMING_INVOICE_EVENT_LABEL = {
	received: "incoming_invoices.events.received",
	read: "incoming_invoices.events.read",
	read_failed: "incoming_invoices.events.read_failed",
	checked: "incoming_invoices.events.checked",
	approval_requested: "incoming_invoices.events.approval_requested",
	approved: "incoming_invoices.events.approved",
	rejected: "incoming_invoices.events.rejected",
	decision_taken_back: "incoming_invoices.events.decision_taken_back",
	state_changed: "incoming_invoices.events.state_changed",
	cost_allocation_suggested: "incoming_invoices.events.cost_allocation_suggested",
	cost_allocation_changed: "incoming_invoices.events.cost_allocation_changed",
	cost_allocation_cleared: "incoming_invoices.events.cost_allocation_cleared",
	field_changed: "incoming_invoices.events.field_changed",
	duplicate_detected: "incoming_invoices.events.duplicate_detected",
	duplicate_dismissed: "incoming_invoices.events.duplicate_dismissed",
	duplicate_merged: "incoming_invoices.events.duplicate_merged",
	merged_into: "incoming_invoices.events.merged_into",
	reminder_received: "incoming_invoices.events.reminder_received",
	approval_withdrawn: "incoming_invoices.events.approval_withdrawn",
	payment_exported: "incoming_invoices.events.payment_exported",
	payment_reverted: "incoming_invoices.events.payment_reverted",
	iban_confirmed: "incoming_invoices.events.iban_confirmed",
	approval_skipped: "incoming_invoices.events.approval_skipped",
	amount_reduced: "incoming_invoices.events.amount_reduced",
	reduction_lifted: "incoming_invoices.events.reduction_lifted",
	delivery_note_linked: "incoming_invoices.events.delivery_note_linked",
	delivery_note_unlinked: "incoming_invoices.events.delivery_note_unlinked",
	approvals_reset: "incoming_invoices.events.approvals_reset",
} as const satisfies Record<IncomingInvoiceEventListItem["type"], TranslationKey>;
